import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    Router,
    RouterStateSnapshot,
    UrlTree
} from '@angular/router';
import { Observable, map, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { EnterpriseFeatureService } from '../services/enterprise-features.service';

@Injectable({
    providedIn: 'root'
})
export class EnterpriseSubscriptionGuard implements CanActivate, CanActivateChild {

    constructor(
        private readonly enterpriseFeatureService: EnterpriseFeatureService,
        private readonly router: Router,
        private readonly messageService: MessageService
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
        return this.checkAccess(route, state);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
        return this.checkAccess(route, state);
    }

    private checkAccess(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
        const requiredModules = this.getRequiredModules(route);
        if (!requiredModules.length) {
            return true;
        }

        if (this.enterpriseFeatureService.isLoaded()) {
            if (!this.enterpriseFeatureService.trialLifecycle()) {
                return this.enterpriseFeatureService.refreshTrialLifecycle().pipe(
                    map(() => this.resolveAccess(requiredModules, route, state))
                );
            }
            return this.resolveAccess(requiredModules, route, state);
        }

        return this.enterpriseFeatureService.getAllSubscriptions().pipe(
            switchMap(() => this.enterpriseFeatureService.refreshTrialLifecycle()),
            map(() => this.resolveAccess(requiredModules, route, state))
        );
    }

    private resolveAccess(
        requiredModules: string[],
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree {
        if (this.hasAnyRequiredModule(requiredModules)) {
            return true;
        }

        const label = route.data['enterpriseFeatureLabel'] ?? this.humanizeModules(requiredModules);
        this.messageService.add({
            severity: 'warn',
            summary: 'Subscription required',
            detail: `Your company is not subscribed to ${label}.`
        });

        return this.router.createUrlTree(['/enterprise-subscription/manage'], {
            queryParams: {
                blockedUrl: state.url
            }
        });
    }

    private hasAnyRequiredModule(requiredModules: string[]): boolean {
        const activeModules = this.enterpriseFeatureService.activeModules();
        return requiredModules.some(moduleCode =>
            activeModules.some(subscription => subscription.module === moduleCode)
        );
    }

    private getRequiredModules(route: ActivatedRouteSnapshot): string[] {
        let cursor: ActivatedRouteSnapshot | null = route;
        while (cursor) {
            const modules = cursor.data['enterpriseModules'];
            if (Array.isArray(modules) && modules.length) {
                return modules;
            }
            cursor = cursor.parent;
        }
        return [];
    }

    private humanizeModules(modules: string[]): string {
        return modules
            .map(module => module.replace(/_/g, ' ').toLowerCase())
            .map(module => module.charAt(0).toUpperCase() + module.slice(1))
            .join(' or ');
    }

}
